import { newUlid } from './ids';
import type { BatchRecord, OutboxOperation } from './records';
import type { TenantDatabase } from './tenant-db';
import {
    MAX_BATCH_SIZE,
    type PushRequest,
    type WireOperation,
    type WireOperationBody,
} from './wire';

/**
 * De outbox: elke wijziging als bewerking in de vorm van het draadformaat, in
 * volgorde, tot de server hem bevestigt.
 */

export interface MutationOperation {
    body: WireOperationBody;
    inspectionUuid: string | null;
    /** Zie `entityKeys` in `sync-records.ts`. */
    entityKey: string;
    /**
     * De server kent deze entiteit nog niet. Een `delete` op een nog niet
     * verstuurde `upsert` heft die dan op: er hoeft niets over de lijn.
     */
    unsynced?: boolean;
    /**
     * Het `client_updated_at` van deze bewerking, als dat later moet zijn dan
     * dat van de mutatie: zie `afterVersion()`.
     */
    clientUpdatedAt?: string;
}

export const DEFAULT_BACKOFF = {
    baseDelayMs: 2_000,
    maxDelayMs: 5 * 60 * 1_000,
} as const;

/** 2 s, 4 s, 8 s … tot hoogstens vijf minuten. `attempts` is het aantal mislukte pogingen. */
export function backoffDelay(
    attempts: number,
    options: { baseDelayMs?: number; maxDelayMs?: number } = {},
): number {
    const base = options.baseDelayMs ?? DEFAULT_BACKOFF.baseDelayMs;
    const max = options.maxDelayMs ?? DEFAULT_BACKOFF.maxDelayMs;

    return Math.min(max, base * 2 ** Math.max(0, attempts - 1));
}

/**
 * Zet een bewerking in de outbox. Staat er voor dezelfde `entityKey` al een
 * bewerking die nog in geen enkele batch zit, dan wordt die vervangen: de
 * server hoeft niet elke tussenstap van een aangetikte optie te zien, alleen
 * de laatste - met de laatste `client_updated_at`.
 *
 * Een bewerking die al in een verstuurde batch zit, blijft onaangeroerd; de
 * nieuwe komt erachter.
 */
export async function enqueueOperation(
    db: TenantDatabase,
    operation: MutationOperation,
    clientUpdatedAt: string,
    createdAt: number,
): Promise<OutboxOperation | null> {
    const at = operation.clientUpdatedAt ?? clientUpdatedAt;
    const unsent = await db.outbox
        .where('entityKey')
        .equals(operation.entityKey)
        .filter(
            (candidate) => candidate.batchUuid === null && sameIdentity(candidate, operation.body),
        )
        .last();

    if (unsent?.seq !== undefined) {
        if (
            operation.body.action === 'delete' &&
            unsent.action === 'upsert' &&
            operation.unsynced === true &&
            !(await isOnItsWay(db, operation))
        ) {
            await db.outbox.delete(unsent.seq);

            return null;
        }

        const replaced: OutboxOperation = {
            ...operation.body,
            seq: unsent.seq,
            opUuid: unsent.opUuid,
            clientUpdatedAt: at,
            inspectionUuid: operation.inspectionUuid,
            entityKey: operation.entityKey,
            status: 'pending',
            batchUuid: null,
            createdAt: unsent.createdAt,
        };

        await db.outbox.put(replaced);

        return replaced;
    }

    const record: OutboxOperation = {
        ...operation.body,
        opUuid: newUlid(),
        clientUpdatedAt: at,
        inspectionUuid: operation.inspectionUuid,
        entityKey: operation.entityKey,
        status: 'pending',
        batchUuid: null,
        createdAt,
    };
    const seq = await db.outbox.add(record);

    return { ...record, seq };
}

/**
 * Zit er al een bewerking op dit ding in een verstuurde batch? Dan kent de server
 * het straks, ook al heeft hij dat nog niet bevestigd: een `delete` moet dan mee,
 * anders zet die batch het op de server en komt het bij de volgende pull terug.
 */
async function isOnItsWay(db: TenantDatabase, operation: MutationOperation): Promise<boolean> {
    const sent = await db.outbox
        .where('entityKey')
        .equals(operation.entityKey)
        .filter(
            (candidate) => candidate.batchUuid !== null && sameIdentity(candidate, operation.body),
        )
        .count();

    return sent > 0;
}

/**
 * Alleen een bewerking op precies hetzelfde ding mag vervangen worden. Een
 * antwoord dat gewist en opnieuw gegeven is, heeft een nieuw `answer_uuid`: de
 * `delete` van het oude antwoord moet dan gewoon mee, vóór de nieuwe `upsert`.
 */
function sameIdentity(candidate: OutboxOperation, body: WireOperationBody): boolean {
    if (candidate.entity === 'inspection_answer' && body.entity === 'inspection_answer') {
        return candidate.payload.answer_uuid === body.payload.answer_uuid;
    }

    /* Starten en afronden zijn twee stappen die de server allebei moet zien. */
    if (candidate.entity === 'inspection' && body.entity === 'inspection') {
        return candidate.payload.status === body.payload.status;
    }

    return candidate.entity === body.entity;
}

/** Van outbox-record naar de bewerking zoals hij in `operations[]` van een push staat. */
export function toWireOperation(operation: OutboxOperation): WireOperation {
    const meta = { op_uuid: operation.opUuid, client_updated_at: operation.clientUpdatedAt };

    switch (operation.entity) {
        case 'inspection':
            return {
                ...meta,
                entity: operation.entity,
                action: 'upsert',
                payload: operation.payload,
            };
        case 'inspection_answer':
            return operation.action === 'upsert'
                ? {
                      ...meta,
                      entity: operation.entity,
                      action: 'upsert',
                      payload: operation.payload,
                  }
                : {
                      ...meta,
                      entity: operation.entity,
                      action: 'delete',
                      payload: operation.payload,
                  };
        case 'multi_count':
            return {
                ...meta,
                entity: operation.entity,
                action: 'upsert',
                payload: operation.payload,
            };
        case 'finding':
            return operation.action === 'upsert'
                ? {
                      ...meta,
                      entity: operation.entity,
                      action: 'upsert',
                      payload: operation.payload,
                  }
                : {
                      ...meta,
                      entity: operation.entity,
                      action: 'delete',
                      payload: operation.payload,
                  };
        case 'order':
            return {
                ...meta,
                entity: operation.entity,
                action: 'upsert',
                payload: operation.payload,
            };
    }
}

export async function pendingOperationCount(db: TenantDatabase): Promise<number> {
    return db.outbox.count();
}

/**
 * De batch die nu verstuurd moet worden.
 *
 * Staat er een batch open (verstuurd, nog geen antwoord), dan is dat die - met
 * exact dezelfde inhoud en `batch_uuid`, zodat de server hem herkent en niets
 * dubbel toepast. Anders wordt een nieuwe batch gevormd uit de eerste
 * {@link MAX_BATCH_SIZE} bewerkingen, in volgorde.
 */
export async function takeBatch(
    db: TenantDatabase,
    deviceUuid: string,
    now: number,
): Promise<BatchRecord | null> {
    return db.transaction('rw', db.outbox, db.batches, async () => {
        const open = (await db.batches.toArray()).sort((a, b) => a.createdAt - b.createdAt);

        if (open[0]) {
            return open[0];
        }

        const operations = await db.outbox
            .filter((operation) => operation.batchUuid === null)
            .limit(MAX_BATCH_SIZE)
            .toArray();

        if (operations.length === 0) {
            return null;
        }

        const batchUuid = newUlid(now);
        const body: PushRequest = {
            batch_uuid: batchUuid,
            device_uuid: deviceUuid,
            operations: operations.map(toWireOperation),
        };
        const batch: BatchRecord = {
            batchUuid,
            opSeqs: operations.flatMap((operation) =>
                operation.seq === undefined ? [] : [operation.seq],
            ),
            body,
            attempts: 0,
            nextAttemptAt: now,
            createdAt: now,
            lastError: null,
        };

        await db.outbox.bulkPut(
            operations.map((operation) => ({ ...operation, status: 'inFlight', batchUuid })),
        );
        await db.batches.put(batch);

        return batch;
    });
}

/** Geeft de bewerkingen van een batch terug aan de wachtrij en vergeet de batch. */
export async function releaseBatch(db: TenantDatabase, batchUuid: string): Promise<void> {
    await db.transaction('rw', db.outbox, db.batches, async () => {
        await db.outbox
            .where('batchUuid')
            .equals(batchUuid)
            .modify({ status: 'pending', batchUuid: null });
        await db.batches.delete(batchUuid);
    });
}
